import React from "react";
import PropTypes from "prop-types";
import { useResizeObserver } from "../hooks/useResizeObserver";

function getAbsoluteElementPosition(rootElement) {
  let element = rootElement;
  let left = 0;
  let top = 0;

  while (element) {
    left += element.offsetLeft;
    top += element.offsetTop;

    element = element.offsetParent;
  }

  return { left, top };
}

const SliderValueContext = React.createContext();
const SliderChangeContext = React.createContext();

export const SliderContextProvider = ({ children }) => {
  const [value, setValue] = React.useState(null);

  return (
    <SliderValueContext.Provider value={value}>
      <SliderChangeContext.Provider value={setValue}>
        {children}
      </SliderChangeContext.Provider>
    </SliderValueContext.Provider>
  );
};

SliderContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Get the ref of the current chapter's slider
 */
export function useSliderRef() {
  const context = React.useContext(SliderValueContext);

  // We're explicity allowing an empty ref here because we'll need a default
  // value before the slider is rendered

  return context;
}

/**
 * Return a function used to set the ref of the current chapter's slider
 */
export function useSetSliderRef() {
  const context = React.useContext(SliderChangeContext);

  if (context == null) {
    throw new Error("useSetSliderRef must be used within a SliderProvider");
  }

  return context;
}

export function WithSetSliderRef({ children }) {
  const setSliderRef = useSetSliderRef();

  return children({ setSliderRef });
}

const useForceUpdate = () => React.useState()[1];

/**
 * Get the current chapter slider's dimensions
 */
export function useSliderDimensions() {
  const forceUpdate = useForceUpdate();
  const sliderRef = useSliderRef();

  // This is here merely to trigger updates when we detect resizes
  useResizeObserver(sliderRef);

  let left = 0;
  let top = 0;
  let width = 0;
  let height = 0;

  if (sliderRef) {
    const absoluteElementPosition =
      sliderRef && getAbsoluteElementPosition(sliderRef);

    left = absoluteElementPosition.left;
    top = absoluteElementPosition.top;
    width = sliderRef.offsetWidth;
    height = sliderRef.offsetHeight;
  }

  // If there are no dimensions, assume that we're using the default empty
  // dimensions. Since there won't be any resize triggered when we finally get
  // a ref, use requestAnimationFrame and `forceUpdate` to keep checking until
  // we have dimensions.
  React.useEffect(() => {
    if (!width && !height) {
      const animationFrame = requestAnimationFrame(forceUpdate);

      return () => {
        cancelAnimationFrame(animationFrame);
      };
    }
  }, [forceUpdate, height, sliderRef, width]);

  return React.useMemo(
    () => new DOMRect(left, top, width, height),
    [height, left, top, width],
  );
}

export const WithSliderDimensions = ({ children }) => {
  const sliderDimensions = useSliderDimensions();

  return children({ sliderDimensions });
};
